import type { CustomerSortField } from '../types/customer';

const STORAGE_KEY = 'brix.customersTable';

export type SortDir = 'asc' | 'desc';

export const CUSTOMER_SORT_FIELDS: readonly CustomerSortField[] = [
  'display_name',
  'email',
  'customer_type',
  'balance',
  'credit_balance',
  'active',
  'created_at',
] as const;

const CUSTOMER_SORT_FIELD_SET = new Set<string>(CUSTOMER_SORT_FIELDS);

export type CustomersTablePreferences = {
  sort: CustomerSortField | `-${CustomerSortField}`;
  perPage: number;
};

const DEFAULT_PREFERENCES: CustomersTablePreferences = {
  sort: 'display_name',
  perPage: 25,
};

export const encodeCustomerSort = (
  field: CustomerSortField,
  direction: SortDir
): CustomersTablePreferences['sort'] => (direction === 'desc' ? `-${field}` : field);

export const parseCustomerSort = (
  value: string
): { field: CustomerSortField; direction: SortDir } | null => {
  const direction: SortDir = value.startsWith('-') ? 'desc' : 'asc';
  const field = (value.startsWith('-') ? value.slice(1) : value) as CustomerSortField;

  if (!CUSTOMER_SORT_FIELD_SET.has(field)) {
    return null;
  }

  return { field, direction };
};

export const loadCustomersTablePreferences = (): CustomersTablePreferences => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(raw) as Partial<CustomersTablePreferences>;
    const sort =
      typeof parsed.sort === 'string' && parseCustomerSort(parsed.sort)
        ? (parsed.sort as CustomersTablePreferences['sort'])
        : DEFAULT_PREFERENCES.sort;
    const perPage =
      typeof parsed.perPage === 'number' && parsed.perPage >= 1 && parsed.perPage <= 100
        ? parsed.perPage
        : DEFAULT_PREFERENCES.perPage;

    return { sort, perPage };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

export const saveCustomersTablePreferences = (prefs: CustomersTablePreferences): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore quota / private browsing errors.
  }
};
